import React, { useState, useEffect } from 'react';
import { useAuth } from '../../shared/hooks';
import { Button, ThemedScreen, Card } from '../../shared/components';
import { EmotionalAlert } from '../../shared/services/notificationService';
import { storageService } from '../../shared/services/storageService';

interface SettingsScreenProps {
  onNavigate: (screen: string) => void;
}

const CALMI_KEYS = [
  'calmi_chats',
  'calmi_current_chat_id',
  'calmi_profile_mode',
  'calmi_child_profile',
  'calmi_tutor_pin',
  'calmi_notifications',
  'calmi_voice',
  'calmi_language',
  'calmi_background_theme',
  'calmi_emotions',
  'calmi_gratitudes',
  'calmi_goals',
  'calmi_drawings',
  'calmi_stories',
  'calmi_thoughts',
  'calmi_alerts'
];

const SettingsScreen: React.FC<SettingsScreenProps> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const [tutorMode, setTutorMode] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');

  const [deletePin, setDeletePin] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const [alerts, setAlerts] = useState<EmotionalAlert[]>([]);

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryPeriod, setSummaryPeriod] = useState<'week' | 'month'>('week');
  const [summaryData, setSummaryData] = useState<Record<string, number>>({});

  const [showTextSizeModal, setShowTextSizeModal] = useState(false);
  const [textSize, setTextSize] = useState<'normal' | 'large' | 'xlarge'>('normal');

  useEffect(() => {
    setTutorMode(localStorage.getItem('calmi_profile_mode') === 'tutor');
    setTextSize((localStorage.getItem('calmi_text_size') as 'normal' | 'large' | 'xlarge') || 'normal');
    loadAlerts();
  }, []);

  const loadAlerts = () => {
    const raw = localStorage.getItem('calmi_alerts');
    if (raw) {
      try {
        const parsed: EmotionalAlert[] = JSON.parse(raw);
        setAlerts(parsed.map(a => ({ ...a, date: new Date(a.date) })));
      } catch {
        setAlerts([]);
      }
    }
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      // Firebase auth will handle the redirect to login screen
    }
  };

  const openSummary = (period: 'week' | 'month') => {
    const days = period === 'week' ? 7 : 30;
    const emotions = storageService.getEmotionsByPeriod(days);
    const counts: Record<string, number> = {};
    emotions.forEach(e => {
      counts[e.emotion] = (counts[e.emotion] || 0) + 1;
    });
    setSummaryPeriod(period);
    setSummaryData(counts);
    setShowSummaryModal(true);
  };

  const applyTextSize = (size: 'normal' | 'large' | 'xlarge') => {
    setTextSize(size);
    localStorage.setItem('calmi_text_size', size);
    const sizeMap = { normal: '100%', large: '112%', xlarge: '125%' };
    document.documentElement.style.fontSize = sizeMap[size];
  };

  const handleChangePin = () => {
    setPinError('');
    const savedPin = localStorage.getItem('calmi_tutor_pin') || '';

    if (savedPin && currentPin !== savedPin) {
      setPinError('El PIN actual es incorrecto.');
      return;
    }

    if (newPin.length < 4) {
      setPinError('El nuevo PIN debe tener al menos 4 dígitos.');
      return;
    }

    if (newPin !== confirmPin) {
      setPinError('Los PINs nuevos no coinciden.');
      return;
    }

    localStorage.setItem('calmi_tutor_pin', newPin);
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setShowPinModal(false);
  };

  const handleExport = () => {
    const data: Record<string, string | null> = {};
    CALMI_KEYS.forEach(key => {
      data[key] = localStorage.getItem(key);
    });

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calmi-respaldo-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportModal(true);
  };

  const handleDeleteData = () => {
    setDeleteError('');
    const savedPin = localStorage.getItem('calmi_tutor_pin') || '';

    if (savedPin && deletePin !== savedPin) {
      setDeleteError('PIN incorrecto.');
      return;
    }

    if (deleteConfirm !== 'BORRAR') {
      setDeleteError('Escribe BORRAR para confirmar.');
      return;
    }

    CALMI_KEYS.forEach(key => localStorage.removeItem(key));
    setDeletePin('');
    setDeleteConfirm('');
    setShowDeleteModal(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  if (!user) {
    return null;
  }

  const displayName = user.displayName || 'Usuario';
  const email = user.email;

  const Modal: React.FC<{ open: boolean; onClose: () => void; children: React.ReactNode; title: string }> = ({ open, onClose, children, title }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <Card className="w-full max-w-sm p-6 bg-white shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-800">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full">✕</button>
          </div>
          {children}
        </Card>
      </div>
    );
  };

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8 pb-24">
      <div className="max-w-md mx-auto">
        <div className="flex items-center mb-6">
          <button onClick={() => onNavigate('home')} className="p-2 sm:p-3 bg-white rounded-full shadow-md mr-4 hover:shadow-lg transition-all">
            <span className="text-xl sm:text-2xl">←</span>
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Configuración</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mr-4">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{displayName}</h2>
              <p className="text-gray-500 text-sm">Cuenta de usuario</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-b border-gray-100 pb-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Correo electrónico</p>
              <p className="text-gray-800 font-medium">{email}</p>
            </div>

            <div className="border-b border-gray-100 pb-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Nombre</p>
              <p className="text-gray-800 font-medium">{displayName}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Preferencias</h3>

          <div className="space-y-3">
            <button
              onClick={() => openSummary('week')}
              className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
            >
              📈 Resumen semanal/mensual
            </button>

            <button
              onClick={() => setShowTextSizeModal(true)}
              className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
            >
              🔠 Tamaño de texto
            </button>

            {tutorMode && (
              <>
                <button
                  onClick={() => setShowPinModal(true)}
                  className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
                >
                  🔒 Cambiar PIN del tutor
                </button>

                <button
                  onClick={handleExport}
                  className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
                >
                  📤 Exportar datos
                </button>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-red-600"
                >
                  🗑️ Borrar datos
                </button>

                <button
                  onClick={() => setShowAlertsModal(true)}
                  className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
                >
                  📋 Ver alertas recientes
                </button>
              </>
            )}

            <button
              onClick={() => setShowInfoModal(true)}
              className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all font-medium text-gray-800"
            >
              ℹ️ Información
            </button>
          </div>
        </div>

        <Button
          onClick={handleLogout}
          className="w-full bg-red-500 hover:bg-red-600 text-white"
        >
          🔴 Cerrar perfil
        </Button>
      </div>

      {/* Modal Cambiar PIN */}
      <Modal open={showPinModal} onClose={() => setShowPinModal(false)} title="Cambiar PIN del tutor">
        <div className="space-y-3">
          <input
            type="password"
            inputMode="numeric"
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="PIN actual"
            className="w-full p-3 border-2 border-slate-200 rounded-xl text-center tracking-widest"
          />
          <input
            type="password"
            inputMode="numeric"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Nuevo PIN"
            className="w-full p-3 border-2 border-slate-200 rounded-xl text-center tracking-widest"
          />
          <input
            type="password"
            inputMode="numeric"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Confirmar nuevo PIN"
            className="w-full p-3 border-2 border-slate-200 rounded-xl text-center tracking-widest"
          />
          {pinError && <p className="text-sm text-red-500 text-center">{pinError}</p>}
          <Button onClick={handleChangePin} className="w-full bg-blue-500 text-white">Guardar PIN</Button>
        </div>
      </Modal>

      {/* Modal Exportar */}
      <Modal open={showExportModal} onClose={() => setShowExportModal(false)} title="Exportar datos">
        <p className="text-slate-700 mb-4">El archivo de respaldo se ha generado y comenzó a descargarse.</p>
        <Button onClick={() => setShowExportModal(false)} className="w-full bg-blue-500 text-white">Aceptar</Button>
      </Modal>

      {/* Modal Borrar datos */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Borrar datos">
        <div className="space-y-3">
          <p className="text-sm text-red-600">⚠️ Esto eliminará todos los datos de Calmi en este dispositivo.</p>
          <input
            type="password"
            inputMode="numeric"
            value={deletePin}
            onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="PIN del tutor"
            className="w-full p-3 border-2 border-slate-200 rounded-xl text-center tracking-widest"
          />
          <input
            type="text"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value.toUpperCase())}
            placeholder="Escribe BORRAR para confirmar"
            className="w-full p-3 border-2 border-slate-200 rounded-xl text-center"
          />
          {deleteError && <p className="text-sm text-red-500 text-center">{deleteError}</p>}
          <Button onClick={handleDeleteData} className="w-full bg-red-500 hover:bg-red-600 text-white">Borrar todo</Button>
        </div>
      </Modal>

      {/* Modal Alertas */}
      <Modal open={showAlertsModal} onClose={() => setShowAlertsModal(false)} title="Alertas recientes">
        <div className="max-h-80 overflow-y-auto space-y-3">
          {alerts.length === 0 ? (
            <p className="text-slate-500 text-center">No hay alertas registradas.</p>
          ) : (
            alerts.slice().reverse().map((alert, idx) => (
              <div key={idx} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-2 ${getSeverityColor(alert.severity)}`}>
                  {alert.severity === 'high' ? 'Alta' : alert.severity === 'medium' ? 'Media' : 'Baja'}
                </div>
                <p className="text-sm text-slate-700">{alert.message}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(alert.date).toLocaleString('es-ES')}</p>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* Modal Información */}
      <Modal open={showInfoModal} onClose={() => setShowInfoModal(false)} title="Información">
        <div className="space-y-3 text-sm text-slate-700">
          <p><strong>Calmi</strong> es un acompañante emocional seguro para niños y niñas de 6 a 12 años.</p>
          <p>La app utiliza IA local (Ollama) para conversar, detectar señales de riesgo y generar alertas para el tutor.</p>
          <p>Todos los datos se guardan localmente en este dispositivo.</p>
          <Button onClick={() => setShowInfoModal(false)} className="w-full bg-blue-500 text-white">Cerrar</Button>
        </div>
      </Modal>

      {/* Modal Resumen emocional */}
      <Modal open={showSummaryModal} onClose={() => setShowSummaryModal(false)} title="Resumen emocional">
        <div className="space-y-3">
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => openSummary('week')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${summaryPeriod === 'week' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-700'}`}
            >
              Semanal
            </button>
            <button
              onClick={() => openSummary('month')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${summaryPeriod === 'month' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-700'}`}
            >
              Mensual
            </button>
          </div>
          {Object.keys(summaryData).length === 0 ? (
            <p className="text-slate-500 text-center py-4">No hay emociones registradas en este período.</p>
          ) : (
            Object.keys(summaryData).map((emotion) => {
              const count = summaryData[emotion];
              return (
                <div key={emotion} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <span className="capitalize font-medium text-slate-700">{emotion}</span>
                  <span className="text-sm font-bold text-slate-900 bg-white px-2 py-1 rounded-md shadow-sm">{count}</span>
                </div>
              );
            })
          )}
        </div>
      </Modal>

      {/* Modal Tamaño de texto */}
      <Modal open={showTextSizeModal} onClose={() => setShowTextSizeModal(false)} title="Tamaño de texto">
        <div className="space-y-3">
          {(['normal', 'large', 'xlarge'] as const).map((size) => (
            <button
              key={size}
              onClick={() => applyTextSize(size)}
              className={`w-full p-3 rounded-xl border-2 text-left font-medium transition-all ${
                textSize === size ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {size === 'normal' && 'Normal'}
              {size === 'large' && 'Grande'}
              {size === 'xlarge' && 'Extra grande'}
            </button>
          ))}
          <p className="text-xs text-slate-500 text-center">El cambio se aplica en toda la app.</p>
        </div>
      </Modal>

    </ThemedScreen>
  );
};

export default SettingsScreen;
